import { Form, Formik, FormikConfig, FormikHelpers } from 'formik'
import { SubmitChecker, SubmitCheckerProps, WrapperProps } from '.'
import { useFetch } from '../hooks'
import { Query } from '../utils/classes/graphql'

interface UpdateFormProps<ValuesType, VariblesType> extends WrapperProps {
  createVariablesFn: (values: ValuesType) => VariblesType
  formikProps: Omit<FormikConfig<ValuesType>, 'enableReinitialize' | 'onSubmit'>
  noValidate?: boolean
  query: string
  resetFormOnSubmit?: boolean
  submitCheckerProps?: Omit<SubmitCheckerProps<Query>, 'fetchState'>
}

export const UpdateForm = <ValuesType, VariblesType>({
  children,
  createVariablesFn,
  formikProps,
  noValidate = true,
  query,
  resetFormOnSubmit = false,
  submitCheckerProps
}: UpdateFormProps<ValuesType, VariblesType>) => {
  const { fetchState, execute } = useFetch<Query, VariblesType>({
    query: query,
    immediate: false
  })

  const handleSubmit = async (
    values: ValuesType,
    { resetForm, setSubmitting }: FormikHelpers<ValuesType>
  ) => {
    setSubmitting(true)

    // All values must be declared in api's input (you could, but you don't have to pass all the values to api, like is done in here)
    const variables: VariblesType = createVariablesFn(values)

    await execute(variables)

    setSubmitting(false)

    if (resetFormOnSubmit) {
      resetForm({ values: formikProps.initialValues })
    }
  }

  return (
    <Formik {...formikProps} enableReinitialize={true} onSubmit={handleSubmit}>
      {(formik) => {
        return (
          <Form noValidate={noValidate} onSubmit={formik.handleSubmit}>
            {children}
            {submitCheckerProps && (
              <SubmitChecker {...{ fetchState, ...submitCheckerProps }} />
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
